/**
 * SectionFigure — a section's headline number with one line about it
 * ("8.2 h" over "312 h turned by 9 techs · account avg 7.9 h"). The number
 * is the `figure` role (the 20px compact figure in compact density, like
 * StatTile); the note is `meta`, or small attention text (`textAccent`)
 * when `noteTone="bad"`. Same piece and props as the app's.
 */
import React from 'react';
import { compactSpec, textRole, tokenVars, type Density } from '@turn-connect/ui';
import { useDensity } from './density';

export interface SectionFigureProps {
  value: React.ReactNode;
  note?: React.ReactNode;
  /** `bad`: the note asks for attention. */
  noteTone?: 'bad';
  density?: Density;
  testId?: string;
}

/** The figure role, at the compact size when compact (shared with StatTile). */
export const figureStyle = (compact: boolean, color?: string): React.CSSProperties => ({
  ...textRole('figure', color),
  ...(compact ? { fontSize: compactSpec.figure.fontSize, lineHeight: `${compactSpec.figure.lineHeight}px` } : null),
});

export const SectionFigure: React.FC<SectionFigureProps> = ({ value, note, noteTone, density, testId }) => {
  const compact = useDensity(density) === 'compact';
  return (
    <div data-testid={testId} style={{ display: 'grid', gap: 2, minWidth: 0 }}>
      <span data-testid={testId ? `${testId}-value` : undefined} style={figureStyle(compact)}>
        {value}
      </span>
      {note !== undefined && note !== null && note !== '' && (
        <span
          data-testid={testId ? `${testId}-note` : undefined}
          style={noteTone === 'bad' ? { ...textRole('meta', tokenVars.textAccent), fontWeight: 600 } : textRole('meta')}
        >
          {note}
        </span>
      )}
    </div>
  );
};

export default SectionFigure;
